import { Code } from 'constants/keyboard';
import isEqual from 'lodash.isequal';
import {
  FocusEvent,
  KeyboardEvent,
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AutoCompleteInputContextI, AutoCompleteInputProviderProps, Item } from './AutoCompleteInput.types';

export const AutoCompleteInputContext = createContext<AutoCompleteInputContextI>({
  selectedIds: [],
  items: [],
  value: '',
  isListVisible: false,
  activeIndex: null,
  activeDescendentId: '',
  setValue: () => {},
  setConfig: () => {},
  addSelectedId: () => {},
  removeSelectedId: () => {},
  setItems: () => {},
  onKeyDown: () => {},
  onInputBlur: () => {},
  onInputFocus: () => {},
  onClearInput: () => {},
  inputRef: null,
  listRef: null,
});

export const AutoCompleteInputProvider = ({
  children,
  submitValueOnEnter = false,
  openListOnFocus = false,
}: AutoCompleteInputProviderProps): JSX.Element => {
  const [config, setConfig] = useState({ isMultiple: false });
  const [inputValue, setInputValue] = useState('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [isListVisible, setIsListVisible] = useState(false);

  const activeDescendentId = useId();

  const filteredItems = useMemo(
    () =>
      items.filter(
        (item) =>
          !selectedIds.includes(item.value) && item.name.toString().toLowerCase().includes(inputValue.toLowerCase()),
      ),
    [items, inputValue, selectedIds],
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<(HTMLUListElement & HTMLDivElement) | null>(null);

  const handleSetInputValue = useCallback(
    (value: string) => {
      setIsListVisible(!!value);
      setInputValue(value);
    },
    [setInputValue],
  );

  // Add the selected item by id
  const handleAddSelectedId = useCallback(
    (itemValue: string) => {
      if (config.isMultiple) {
        setSelectedIds((prev) => [...new Set([...prev, itemValue])]);
        setInputValue('');
      } else {
        setSelectedIds([itemValue]);
        setInputValue(items.find((item) => item.value === itemValue)?.name.toString() || itemValue);
      }
      setIsListVisible(false);
      inputRef.current?.focus();
    },
    [items, config, setSelectedIds],
  );

  // Remove the selected item by id
  const handleRemoveSelectedId = useCallback(
    (itemValue: string) => {
      setSelectedIds((prev) => prev.filter((id) => id !== itemValue));
      inputRef.current?.focus();
    },
    [setSelectedIds],
  );

  // Set item data
  // it generates from the AutoCompleteInputList component using the children componetn
  const handleSetItems = useCallback(
    (items: Item[]) => {
      // Remove id from the selectedIds when the items changed)
      const filteredSelectedIds = selectedIds.filter((id) => items.find((group) => group.value === id));
      if (!isEqual(filteredSelectedIds, selectedIds)) setSelectedIds(filteredSelectedIds);

      setActiveIndex(null);
      setItems(items);
    },
    [setItems, selectedIds, setSelectedIds],
  );

  // Open list of `openListOnFocus` is enabled
  const handleInputFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (openListOnFocus) {
        setIsListVisible(true);
        e.preventDefault();
      }
    },
    [openListOnFocus],
  );

  // Hide the list if the input is blurred
  const handleInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (!listRef?.current?.contains(e.relatedTarget)) {
        setIsListVisible(false);
      }
    },
    [listRef],
  );

  // Clear the input value and trigger the onChange prop with empty string
  const handleClearInput = useCallback(() => {
    setInputValue('');
    handleAddSelectedId('');
  }, [handleAddSelectedId]);

  // For the a11y on the auto complete input
  // @see {https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-autocomplete-list/}
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!inputRef?.current || !listRef?.current) return;

      // Only apply this listener if the input or list is focused
      const target = e.target as Node;
      if (!inputRef?.current?.contains(target) && !listRef?.current?.contains(target)) return;

      const { key, altKey } = e;
      const isShift = e.shiftKey;

      if (!isListVisible) {
        switch (key) {
          case Code.Escape:
            setInputValue('');
            setActiveIndex(null);
            e.preventDefault();
            break;
          case Code.ArrowDown:
            if (altKey) {
              setIsListVisible(true);
            } else if (!inputValue) {
              setActiveIndex(0);
              setIsListVisible(true);
            }
            e.preventDefault();
            break;
          case Code.ArrowUp:
            if (!inputValue) {
              setActiveIndex(filteredItems.length - 1);
              setIsListVisible(true);
            }
            e.preventDefault();
            break;
          case Code.Enter:
            if (inputValue && submitValueOnEnter) {
              handleAddSelectedId(inputValue);
            }
            e.preventDefault();
            break;
          default:
            break;
        }
      } else {
        switch (key) {
          case Code.Enter:
            if (activeIndex === null) {
              setIsListVisible(false);
              if (inputValue && submitValueOnEnter) {
                handleAddSelectedId(inputValue);
              }
            } else {
              handleAddSelectedId(filteredItems[activeIndex].value);
            }

            e.preventDefault();
            break;
          case Code.Escape:
            setIsListVisible(false);
            inputRef.current?.focus();
            e.preventDefault();
            break;
          case Code.Home:
            inputRef.current?.setSelectionRange(0, 0);
            inputRef.current?.focus();
            e.preventDefault();
            break;
          case Code.End:
            inputRef.current?.setSelectionRange(inputRef.current?.value?.length, inputRef.current?.value?.length);
            inputRef.current?.focus();
            e.preventDefault();
            break;
          case Code.ArrowLeft:
            if (isShift) break;

            const prevCursorIndex =
              (inputRef.current?.selectionStart ?? 0) - 1 <= 0 ? 0 : (inputRef.current?.selectionStart ?? 0) - 1;
            inputRef.current?.setSelectionRange(prevCursorIndex, prevCursorIndex);
            inputRef.current?.focus();
            e.preventDefault();
            break;
          case Code.ArrowRight:
            if (isShift) break;

            const valueLength = inputRef.current?.value?.length;
            const nextCursorIndex =
              (inputRef.current?.selectionStart ?? 0) + 1 >= valueLength
                ? valueLength
                : (inputRef.current?.selectionStart ?? 0) + 1;

            inputRef.current?.setSelectionRange(nextCursorIndex, nextCursorIndex);
            inputRef.current?.focus();
            e.preventDefault();
            break;
          case Code.ArrowDown:
            const nextIndex = activeIndex === null || activeIndex >= filteredItems.length - 1 ? 0 : activeIndex + 1;
            setActiveIndex(nextIndex);
            e.preventDefault();
            break;
          case Code.ArrowUp:
            const prevIndex = activeIndex === null || activeIndex <= 0 ? filteredItems.length - 1 : activeIndex - 1;
            setActiveIndex(prevIndex);
            e.preventDefault();
            break;
          default:
            if (activeIndex !== null) {
              setActiveIndex(null);
            }
            break;
        }
      }
    },
    [activeIndex, filteredItems, inputValue, submitValueOnEnter, isListVisible, handleAddSelectedId],
  );

  const value = useMemo(
    () => ({
      selectedIds,
      items,
      value: inputValue,
      isListVisible,
      activeIndex,
      activeDescendentId,
      setValue: handleSetInputValue,
      setConfig: setConfig,
      addSelectedId: handleAddSelectedId,
      removeSelectedId: handleRemoveSelectedId,
      setItems: handleSetItems,
      onKeyDown: handleKeyDown,
      onInputFocus: handleInputFocus,
      onInputBlur: handleInputBlur,
      onClearInput: handleClearInput,
      inputRef,
      listRef,
    }),
    [
      selectedIds,
      items,
      inputValue,
      isListVisible,
      activeIndex,
      activeDescendentId,
      handleSetInputValue,
      handleAddSelectedId,
      handleRemoveSelectedId,
      handleSetItems,
      handleKeyDown,
      handleInputFocus,
      handleInputBlur,
      handleClearInput,
    ],
  );

  return <AutoCompleteInputContext.Provider value={value}>{children}</AutoCompleteInputContext.Provider>;
};

export const useAutoCompleteContext = () => {
  const context = useContext(AutoCompleteInputContext);
  if (!context) {
    throw new Error('AutoCompleteInputContext must be used within AutoCompleteInputProvider');
  }

  return context;
};
